import { useEffect, useState } from 'react';
import {
	FormControl,
	InputLabel,
	OutlinedInput,
} from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid';
import {
	getTotalReconciliations,
	listReconciledTransactionDetails,
	loadReconciliationsList,
	saveReconciliationService,
} from '../services/reconciliation.service';
import { Visibility } from '@mui/icons-material';
import 'dayjs/locale/en-gb';

import {
	formatCurrency,
	formatDate,
	isNullUndefined,
} from '../util/util';
import {issueResponseMessage, issueWarnMessage} from '../actions/message';
import { printReconciliation } from '../services/print.service';
import { useSelector } from 'react-redux';
import {
	MainContainer,
	TopBarContainedBrandButton,
	TopBarContainer,
} from '../constants/component.constants';
import { ResponsiveRow } from '../constants/layout.constants';
import CreateReconciliationModal from '../components/modals/accounting/reconcilation/createReconciliationModal';
import ViewReconcilationModal from '../components/modals/accounting/reconcilation/viewReconcilationModal';
import Permit from '../util/permit';
import MuiDataGrid from '../components/modules/MuiDataGrid';

export default function Reconciliation() {
	const user = useSelector((state) => state.auth.user);
	const [accountName, setAccountName] = useState("");
	const [reconciledBy, setReconciledBy] = useState("");
	const [paginationModel, setPaginationModel] = useState({
		page: 0,
		pageSize: 50,
	});
	const [reconciliationList, setReconciliationList] = useState([]);
	const [selectionModel, setSelectionModel] = useState([]);
	const [totalReconciliations, setTotalReconciliations] = useState(0);
	const [reconciliationForm, setReconciliationForm] = useState(false);
	const [selectedReconciliation, setSelectedReconciliation] = useState(null);
	const [reconciliationView, setReconciliationView] = useState(false);
	const [reconciledTransactionDetails, setReconciledTransactionDetails] = useState([]);
	const [reconciledTransactionDetailsTotal, setReconciledTransactionDetailsTotal] = useState(0);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		if(!reconciliationForm) {
			setLoading(true);
			loadReconciliationsList(accountName, reconciledBy, paginationModel.page)
				.then((data) => setReconciliationList(data))
			getTotalReconciliations(accountName, reconciledBy)
				.then((data) => setTotalReconciliations(data.totalReconciliations))
				.finally(() => {
					setLoading(false);
				});
		}
	}, [accountName, paginationModel.page, reconciledBy, user.proxyFacility, reconciliationForm]);

	function handleViewClicked(item) {
		listReconciledTransactionDetails(item.id, paginationModel.page)
			.then((data) => {
				setReconciledTransactionDetails(data);
				setReconciledTransactionDetailsTotal(data.length);
			})
		setSelectedReconciliation(item);
		setReconciliationView(true);
	}

	function handlePrintReconciliation() {
		printReconciliation(selectedReconciliation.id)
			.then((response) => {
				setReconciliationView(false);
				issueResponseMessage(response)
			})
	}

	function handleCloseView() {
		setSelectedReconciliation(null);
		setReconciliationView(false);
		setSelectionModel([]);
		setReconciledTransactionDetails([]);
	}

	const columns = [
		{
			headerName: "Bank Account",
			field: "account",
			minWidth: 150,
			fleX: 1.5,
			renderCell: (params) => params.value.name,
		},
		{
			headerName: "Ledger Account(s)",
			field: "ledgerAccounts",
			minWidth: 150,
			flex: 1.5,
			renderCell: (params) => params.value.names,
		},
		{ headerName: "Start Date", field: "startDate", width: 120 },
		{ headerName: "Statement Date", field: "statementDate", width: 120 },
		{ headerName: "Beginning Balance", field: "begBalance", width: 140 },
		{ headerName: "End Balance", field: "endBalance", width: 140 },
		{ headerName: "Recon Balance", field: "reconBalance", width: 140 },
		{ headerName: "Balance Diff", field: "balanceDiff", width: 139 },
		{
			headerName: "Reconciled By",
			field: "reconciledBy",
			minWidth: 150,
			flex: 1.5,
			renderCell: (params) => params.value.name,
		},
		{
			headerName: "Actions",
			field: "actions",
			width: 100,
			type: "actions",
			getActions: (params) => {
				let arr = [];
				arr.push(
					<GridActionsCellItem
						icon={<Visibility />}
						onClick={() => handleViewClicked(params.row)}
						label="View"
						showInMenu
					/>
				);

				return arr;
			},
		},
	];

	const reconciledTransactionDetailsColumns = [
		{
			headerName: 'Date',
			field: 'entryDate',
			width: 150,
			valueFormatter: (row) => {
				return formatDate(row.value);
			},
		},
		{ headerName: 'Type', field: 'type', minWidth: 200, flex: 1 },
		{ headerName: 'Details', field: 'description', minWidth: 400, flex: 2.8 },
		{
			headerName: 'Debit Amount',
			field: 'debitAmount',
			minWidth: 150,
			flex: 1,
			valueFormatter: (row) => {
				return formatCurrency(row.value);
			},
		},
		{
			headerName: 'Credit Amount',
			field: 'creditAmount',
			minWidth: 150,
			flex: 1,
			valueFormatter: (row) => {
				return formatCurrency(row.value);
			},
		},
	];

	function handleAddReconciliationClicked() {
		setReconciliationForm(true);
	}

	function handleCloseReconciliationClicked() {
		setSelectedReconciliation(null);
		setSelectionModel([]);
		setReconciliationForm(false);
	}

	function saveReconciliation(data) {
		if (isNullUndefined(data)) {
			issueWarnMessage("Could not determine Item to reconcile. Try to refresh the page or contact support.")
			return false;
		}
		setLoading(true);
		saveReconciliationService(data)
			.then((response) => {
				issueResponseMessage(response)
				handleCloseReconciliationClicked();
			}).finally(() => setLoading(false));
	}

	return (
		<Permit
			roles="BLDIR,ACC,BLDMG,CS"
			noPermittedServiceMessage={{
				title: 'Access Denied',
				body: (
					<>
						Your current role is not permitted to view this page. <br /> Please
						contact your system admin if you feel this is an error.
					</>
				),
			}}
		>
			<MainContainer>
				<TopBarContainer
					container
					sx={{
						justifyContent: { xs: 'center', lg: 'space-between' },
						width: { xs: '100%', md: '80%' },
						padding: '0 10px',
						boxShadow: '0 4px 5px 0 rgba(0,0,0,0.5)',
					}}
				>
					<ResponsiveRow
						sx={{
							flexWrap: { xs: 'wrap', lg: 'nowrap' },
							justifyContent: { md: 'flex-start', xs: 'space-around' },
							alignItems: 'center',
							width: { lg: '50%', sm: '100%' },
							height: 'auto',
						}}
					>
						<FormControl
							sx={{
								marginBottom: '10px',
								width: { xs: '100%', lh: '50%' },
							}}
						>
							<InputLabel id="bankAcc">Bank Account Name</InputLabel>
							<OutlinedInput
								id="searchBAnkAccNamed"
								value={accountName}
								label={`Bank Account Name`}
								sx={{ width: { xs: '100%', lg: '90%' } }}
								onChange={(event) => {
									let value = event.target.value;
									setAccountName(value);
								}}
							/>
						</FormControl>

						<FormControl
							sx={{
								marginBottom: '10px',
								width: '100%',
							}}
						>
							<InputLabel id="reconciledBy">Reconciled By</InputLabel>
							<OutlinedInput
								id="reconciledBy"
								value={reconciledBy}
								label={`Reconciled By`}
								sx={{ width: '100%' }}
								onChange={(event) => {
									let value = event.target.value;
									setReconciledBy(value);
								}}
							/>
						</FormControl>
					</ResponsiveRow>

					<ResponsiveRow
						sx={{
							justifyContent: { md: 'flex-end', xs: 'center' },
							width: { md: '50%', xs: '100%' },
							height: 'auto',
						}}
					>
						<TopBarContainedBrandButton
							variant="contained"
							sx={{
								width: { xs: '100%', lg: '250px' },
								height: '100%',
							}}
							onClick={handleAddReconciliationClicked}
						>
							Add Reconciliation Item
						</TopBarContainedBrandButton>
					</ResponsiveRow>
				</TopBarContainer>

				<br />

				<MuiDataGrid
					dataGridColumns={columns}
					dataGridRows={reconciliationList}
					serverPagination={true}
					handlePageLoad={setPaginationModel}
					handleSelectedRows={setSelectionModel}
					currentSelectionModel={selectionModel}
					loading={loading}
					height="60vh"
					totalRows={totalReconciliations}
				/>

				{
					reconciliationForm &&
					(<CreateReconciliationModal
					reconciliationForm={reconciliationForm}
					handleCloseReconciliationClicked={handleCloseReconciliationClicked}
					loading={loading}
					setLoading={setLoading}
					setPaginationModel={setPaginationModel}
					saveReconciliation={saveReconciliation}
					reconciledTransactionDetailsColumns={reconciledTransactionDetailsColumns}
				/>)
				}

				{reconciliationView && (
					<ViewReconcilationModal
						handlePrintReconciliation={handlePrintReconciliation}
						handleCloseView={handleCloseView}
						setSelectionModel={setSelectionModel}
						loading={loading}
						loadPage={setPaginationModel}
						reconciliationView={reconciliationView}
						reconciledTransactionDetails={reconciledTransactionDetails}
						reconciledTransactionDetailsColumns={reconciledTransactionDetailsColumns}
						reconciledTransactionDetailsTotal={reconciledTransactionDetailsTotal}
						rowsPerPage={paginationModel.pageSize}
						selectedReconciliation={selectedReconciliation}
						selectionModel={selectionModel}
					/>
				)}
			</MainContainer>
		</Permit>
	);
}
